import { useCallback, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Thread } from 'models/inbox/Threads';
import InboxContext from '../InboxContext';
import {
  ThreadInfoContainer,
  ThreadInfoParticipantName,
  ThreadsListItem,
} from './InboxThreads.styles';
import useThreadParticipantBestProfile from './useThreadParticipantBestProfile';
import ThreadAvatar from './ThreadAvatar';
import useThreadReadStatus from './useThreadReadStatus';
import { getThreadLastUpdateDate } from './InboxThreads.utils';

const InboxThread = ({ thread }: { thread: Thread }) => {
  const { uid: threadUid } = thread;
  const { activeThread } = useContext(InboxContext);
  const bestProfile = useThreadParticipantBestProfile(threadUid);
  const { t } = useTranslation();
  const activeThreadUid = activeThread?.uid;
  const active = activeThreadUid === threadUid;
  const { unread, markAsRead } = useThreadReadStatus(thread);
  const { name } = bestProfile;

  const onClick = useCallback(
    (event: React.MouseEvent) => {
      if (active) {
        event.preventDefault();
      }
    },
    [active],
  );

  useEffect(() => {
    if (active && unread && document.hasFocus()) {
      markAsRead();
    }
  }, [active, unread]);

  return (
    <ThreadsListItem
      active={active}
      className={`list-group-item ${active ? 'active' : ''}`}
      // react-bootstrap 0.33 types MouseEventHandler<ListGroupItem> instead of <Element>,
      // which cannot be satisfied by a real handler under styled-components v6's strict typing.
      onClick={onClick as unknown as React.MouseEventHandler<never>}
      data-testid={`thread-list-item-${threadUid}`}
      data-thread-unread={unread}
      href={`?threadUid=${threadUid}`}
    >
      <ThreadAvatar bestProfile={bestProfile} />
      <ThreadInfoContainer unread={unread}>
        <ThreadInfoParticipantName title={name}>
          {name}
        </ThreadInfoParticipantName>
        <div>
          {t('pageInbox.threadsList.lastUpdateDate', {
            date: getThreadLastUpdateDate(thread),
          })}
        </div>
      </ThreadInfoContainer>
    </ThreadsListItem>
  );
};

export default InboxThread;
